import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { Plus, Edit3, Eye, Trash2, Search, X, RefreshCw, FileText } from 'lucide-react';
import DocumentosClienteBox from '../../components/shared/DocumentosClienteBox';
import BoletosErpBox from '../../components/shared/BoletosErpBox';

function formatPhone(value) {
  // Remove tudo que não é número
  const digits = value.replace(/\D/g, '');
  // Formata como (XX) XXXXX-XXXX
  if (digits.length <= 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, ddd, part1, part2) => {
      if (part2) return `(${ddd}) ${part1}-${part2}`;
      if (part1) return `(${ddd}) ${part1}`;
      if (ddd) return `(${ddd}`;
      return '';
    });
  }
  return value;
}

function isValidEmail(email) {
  return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function ClienteForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || { nome: '', cpf: '', email: '', whatsapp: '', endereco: '', estado_civil: '', profissao: '', conjuge_nome: '', conjuge_cpf: '', conjuge_profissao: '' });
  const [errors, setErrors] = useState({});
  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleWhatsapp = (e) => {
    const formatted = formatPhone(e.target.value);
    setForm(p => ({ ...p, whatsapp: formatted }));
  };

  const validate = () => {
    const errs = {};
    if (form.email && !isValidEmail(form.email)) {
      errs.email = 'Email inválido';
    }
    if (form.whatsapp && form.whatsapp.replace(/\D/g, '').length < 10) {
      errs.whatsapp = 'Telefone deve ter pelo menos 10 dígitos';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form);
  };

  return (
    <div className="bg-white rounded-xl shadow p-6 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome *</label><input className="input-field" value={form.nome} onChange={set('nome')} /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CPF</label><input className="input-field" value={form.cpf || ''} onChange={set('cpf')} placeholder="000.000.000-00" /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CNPJ (se PJ)</label><input className="input-field" value={form.cnpj || ''} onChange={set('cnpj')} placeholder="00.000.000/0000-00" /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">RG</label><input className="input-field" value={form.rg || ''} onChange={set('rg')} /></div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
          <input type="email" className={`input-field ${errors.email ? 'border-red-500' : ''}`} value={form.email} onChange={set('email')} />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp</label>
          <input type="tel" className={`input-field ${errors.whatsapp ? 'border-red-500' : ''}`} value={form.whatsapp} onChange={handleWhatsapp} placeholder="(00) 00000-0000" />
          {errors.whatsapp && <p className="text-xs text-red-500 mt-1">{errors.whatsapp}</p>}
        </div>
        <div className="col-span-2"><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Endereço</label><input className="input-field" value={form.endereco} onChange={set('endereco')} /></div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Estado Civil</label>
          <select className="input-field" value={form.estado_civil} onChange={set('estado_civil')}>
            <option value="">Selecione...</option><option value="solteiro">Solteiro(a)</option><option value="casado">Casado(a)</option><option value="divorciado">Divorciado(a)</option><option value="viuvo">Viúvo(a)</option><option value="uniao_estavel">União Estável</option>
          </select>
        </div>
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Profissão</label><input className="input-field" value={form.profissao} onChange={set('profissao')} /></div>
      </div>
      {(form.estado_civil === 'casado' || form.estado_civil === 'uniao_estavel') && (
        <>
          <hr />
          <h4 className="font-bold text-sm">Cônjuge</h4>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome</label><input className="input-field" value={form.conjuge_nome} onChange={set('conjuge_nome')} /></div>
            <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CPF</label><input className="input-field" value={form.conjuge_cpf} onChange={set('conjuge_cpf')} /></div>
            <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Profissão</label><input className="input-field" value={form.conjuge_profissao} onChange={set('conjuge_profissao')} /></div>
          </div>
        </>
      )}
      <div className="flex gap-3 pt-2">
        <button onClick={handleSave} className="btn-primary">Salvar</button>
        <button onClick={onCancel} className="btn-secondary">Cancelar</button>
      </div>
    </div>
  );
}

async function baixarContratoAssinado(contratoId, nome) {
  try {
    const res = await api.get(`/contratos/${contratoId}/clicksign-assinado`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = nome || 'contrato-assinado.pdf';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    alert('Erro ao baixar o contrato: ' + (err.response?.data?.error || err.message));
  }
}

const ESTADO_CIVIL_LABEL = { solteiro: 'Solteiro(a)', casado: 'Casado(a)', divorciado: 'Divorciado(a)', viuvo: 'Viúvo(a)', uniao_estavel: 'União Estável' };

function Campo({ label, value }) {
  return (
    <div>
      <div className="text-xs font-bold text-gray-500 uppercase mb-1">{label}</div>
      <div className="text-sm text-gray-800">{value || <span className="text-gray-300">—</span>}</div>
    </div>
  );
}

// Painel só-leitura: dados do cliente + documentos anexados. Diferente do "Editar" (que abre o
// formulário) — pra quem só quer conferir cadastro/documentos sem risco de mexer em nada.
function ClienteDetalhes({ cliente, onClose }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold">Detalhes do Cliente</h1>
        <button onClick={onClose} className="btn-secondary flex items-center gap-2"><X size={15} /> Fechar</button>
      </div>
      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Campo label="Nome" value={cliente.nome} />
          {cliente.cnpj && !cliente.cpf
            ? <Campo label="CNPJ" value={cliente.cnpj} />
            : <Campo label="CPF" value={cliente.cpf} />}
          {cliente.rg && <Campo label="RG" value={cliente.rg} />}
          <Campo label="Email" value={cliente.email} />
          <Campo label="WhatsApp" value={cliente.whatsapp} />
          <div className="col-span-2"><Campo label="Endereço" value={cliente.endereco} /></div>
          <Campo label="Estado Civil" value={ESTADO_CIVIL_LABEL[cliente.estado_civil] || cliente.estado_civil} />
          <Campo label="Profissão" value={cliente.profissao} />
        </div>
        {(cliente.conjuge_nome || cliente.conjuge_cpf) && (
          <>
            <hr />
            <h4 className="font-bold text-sm">Cônjuge</h4>
            <div className="grid grid-cols-2 gap-4">
              <Campo label="Nome" value={cliente.conjuge_nome} />
              <Campo label="CPF" value={cliente.conjuge_cpf} />
              <Campo label="Email" value={cliente.conjuge_email} />
              <Campo label="WhatsApp" value={cliente.conjuge_whatsapp} />
              <Campo label="Profissão" value={cliente.conjuge_profissao} />
            </div>
          </>
        )}
        {cliente.compras && cliente.compras.length > 0 && (
          <>
            <hr />
            <h4 className="font-bold text-sm">Imóveis comprados</h4>
            <div className="space-y-2">
              {cliente.compras.map((v, i) => (
                <div key={i} className="text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{v.empreendimento || '—'}</span>
                    {v.unidade ? <span>· un. {v.unidade}</span> : null}
                    {v.valor_total ? <span className="text-gray-500">· {Number(v.valor_total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span> : null}
                    {v.status === 'assinado'
                      ? <span className="text-green-600">✓ assinado{v.origem === 'legado' ? ' (contrato antigo)' : ''}</span>
                      : <span className="text-gray-400">({v.status})</span>}
                    {v.pdf_assinado && (
                      <button onClick={() => baixarContratoAssinado(v.contrato_id, `${(v.numero || 'contrato').replace(/[^A-Za-z0-9-]+/g, '_')}-assinado.pdf`)}
                        className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-0.5 hover:bg-green-100">
                        <FileText size={12} /> PDF assinado
                      </button>
                    )}
                  </div>
                  {v.resumo_texto && (
                    <details className="mt-1">
                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Resumo do contrato</summary>
                      <div className="mt-1 text-xs text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap leading-relaxed">{v.resumo_texto}</div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="bg-white rounded-xl shadow p-6 mt-4">
        <h4 className="font-bold text-sm mb-3">Documentos</h4>
        <DocumentosClienteBox clienteId={cliente.id} />
      </div>
      <div className="bg-white rounded-xl shadow p-6 mt-4">
        <h4 className="font-bold text-sm mb-1">Boletos (financeiro)</h4>
        <p className="text-xs text-gray-400 mb-3">Importados pelo financeiro no ERP — o cliente recebe o aviso antes e um lembrete no dia do vencimento.</p>
        <BoletosErpBox baseUrl={`/clientes/${cliente.id}`} />
      </div>
    </div>
  );
}

export default function ClientesList() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ['clientes', search], queryFn: () => api.get('/clientes', { params: { search } }).then(r => r.data.data) });
  const syncMut = useMutation({
    mutationFn: () => api.post('/clientes/sincronizar-vendas').then(r => r.data),
    onSuccess: (r) => { qc.invalidateQueries(['clientes']); alert(`${r.criados} cliente(s) criado(s), ${r.atualizados} atualizado(s).${r.falhas?.length ? ` ${r.falhas.length} sem dados de comprador.` : ''}`); },
    onError: (err) => alert('Erro: ' + (err.response?.data?.error || err.message)),
  });
  const createMut = useMutation({ mutationFn: (d) => api.post('/clientes', d), onSuccess: () => { qc.invalidateQueries(['clientes']); setCreating(false); } });
  const updateMut = useMutation({ mutationFn: ({ id, ...d }) => api.put(`/clientes/${id}`, d), onSuccess: () => { qc.invalidateQueries(['clientes']); setEditing(null); } });
  const deleteMut = useMutation({ mutationFn: (id) => api.delete(`/clientes/${id}`), onSuccess: () => qc.invalidateQueries(['clientes']) });

  if (creating) return (<div><div className="flex items-center justify-between mb-6"><h1 className="text-xl font-bold">Novo Cliente</h1></div><ClienteForm onSave={(d) => createMut.mutate(d)} onCancel={() => setCreating(false)} /></div>);
  if (viewing) return <ClienteDetalhes cliente={viewing} onClose={() => setViewing(null)} />;
  if (editing) return (
    <div>
      <div className="flex items-center justify-between mb-6"><h1 className="text-xl font-bold">Editar Cliente</h1></div>
      <ClienteForm initial={editing} onSave={(d) => updateMut.mutate({ id: editing.id, ...d })} onCancel={() => setEditing(null)} />
      <div className="bg-white rounded-xl shadow p-6 mt-4">
        <h4 className="font-bold text-sm mb-3">Documentos</h4>
        <DocumentosClienteBox clienteId={editing.id} />
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-800">Clientes</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => { if (window.confirm('Cadastrar aqui todos os compradores de contratos já assinados que ainda não estão em Clientes?')) syncMut.mutate(); }}
            disabled={syncMut.isPending}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50">
            <RefreshCw size={15} className={syncMut.isPending ? 'animate-spin' : ''} /> {syncMut.isPending ? 'Sincronizando...' : 'Sincronizar vendas assinadas'}
          </button>
          <button onClick={() => setCreating(true)} className="btn-primary flex items-center gap-2"><Plus size={16} /> Novo Cliente</button>
        </div>
      </div>
      <div className="relative mb-4 w-full">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="input-field pl-10 pr-10 w-full"
          style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
          placeholder="Buscar por nome, CPF ou email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><X size={16} /></button>}
      </div>
      {isLoading ? <div className="text-center text-gray-400 py-12">Carregando...</div> : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Nome</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">CPF</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Cônjuge</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Imóveis comprados</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">WhatsApp</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Ações</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {(data || []).filter(c => c.ativo !== false).map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">
                    {c.nome}
                    <div className="text-xs text-gray-400">{c.email || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{c.cpf || c.cnpj || '—'}</td>
                  <td className="px-4 py-3 text-gray-500">{c.conjuge_nome || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {(c.compras && c.compras.length) ? (
                      <div className="space-y-0.5">
                        {c.compras.map((v, i) => (
                          <div key={i} className="text-xs">
                            <span className="font-medium">{v.empreendimento || '—'}</span>
                            {v.unidade ? ` · un. ${v.unidade}` : ''}
                            {v.status === 'assinado'
                              ? <span className="ml-1 text-green-600">✓</span>
                              : <span className="ml-1 text-gray-400">({v.status})</span>}
                          </div>
                        ))}
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{c.whatsapp || '—'}</td>
                  <td className="px-4 py-3"><div className="flex gap-2">
                    <button onClick={() => setViewing(c)} className="text-gray-600 hover:text-gray-900" title="Ver detalhes e documentos"><Eye size={15} /></button>
                    <button onClick={() => setEditing(c)} className="text-blue-600 hover:text-blue-800" title="Editar"><Edit3 size={15} /></button>
                    <button onClick={() => { if (window.confirm('Desativar este cliente?')) deleteMut.mutate(c.id); }} className="text-red-500 hover:text-red-700" title="Desativar"><Trash2 size={15} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {(data || []).filter(c => c.ativo !== false).length === 0 && <div className="text-center text-gray-400 py-8">Nenhum cliente encontrado.</div>}
        </div>
      )}
    </div>
  );
}
